import { SignUpInput } from './components/SignUpInput';
import { useTranslations } from '@ifixit/i18n/hooks';
import { useState } from 'react';
import type { FormEvent, ChangeEvent } from 'react';
import { FormError } from '../FormError';
import { SSOLoginButtons } from '../SSOLoginButtons';
import { useEmailValidation } from './hooks/useEmailValidation';
import { Auth } from 'Shared/auth';
import { fireGlobal } from 'Shared/utils/global-events';
import { getErrorMessage } from '../LogIn/LogIn';
import { shopifySsoAndGoToLastPage } from './utils/shopifyUtils';
import { cn } from '@ifixit/ui/lib/utils';

interface EmailProps {
   onNavigateToLogin: (email: string) => void;
   onNavigateToRegister: (email: string) => void;
}

export const Email = ({ onNavigateToLogin, onNavigateToRegister }: EmailProps) => {
   const t = useTranslations('Login');
   const [email, setEmail] = useState<string>((window as any).App?.inviteEmail ?? '');
   const [ssoError, setSsoError] = useState<string | null>(null);

   const { isSubmitting, error, clearError, submitEmail } = useEmailValidation({
      onNavigateToLogin,
      onNavigateToRegister,
   });

   const ssoLoginHandler = (response: any) => {
      (Auth as any).clearCookies();

      if (response.loggedIn) {
         fireGlobal('login.success', response);
         shopifySsoAndGoToLastPage();
      } else {
         setSsoError(getErrorMessage(response, t));
         fireGlobal('login.failure', response);
      }
   };

   const emailFieldProvided = !!(window as any).App?.inviteEmail;

   const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setEmail(value);
      if (error) {
         clearError();
      }
   };

   const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      void submitEmail(email);
   };

   return (
      <div className="email-wrapper flex flex-col gap-6 p-4 md:p-8 bg-white border-1 border-base rounded-base text-base w-full md:w-[384px]">
         <form id="registerForm" onSubmit={handleSubmit} className="email-form w-full">
            <div className="flex flex-col gap-6">
               <SSOLoginButtons
                  loginHandler={ssoLoginHandler}
                  error={ssoError}
                  setError={setSsoError}
                  register={true}
               />
               <div className="flex flex-col gap-3">
                  <div className={cn(error ? 'border-red-500' : '')}>
                     <SignUpInput
                        field={{
                           name: 'email',
                           value: email,
                           onChange: handleEmailChange,
                           onBlur: () => {},
                           type: 'email',
                           required: true,
                           autoComplete: 'section-register email',
                           placeholder: 'email@example.com',
                        }}
                        label={t('email')}
                        id="email"
                        isDisabled={emailFieldProvided}
                     />
                     <FormError>{error}</FormError>
                  </div>
                  <button
                     type="submit"
                     id="registerBtn"
                     className="w-full button-primary button-lg"
                     disabled={isSubmitting}
                  >
                     {t('signUpWithEmail')}
                  </button>
               </div>
            </div>
         </form>
      </div>
   );
};
